import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthReady } from "@/hooks/useAuthReady";

const STORAGE_KEY = "hibane.activeOrgId";
const CHANNEL = "hibane-org-state";

export interface OrgMembership {
  org_id: string;
  name: string;
  slug: string | null;
  logo_url: string | null;
  member_role: string | null;
  joined_at: string | null;
}

interface ActiveOrgState {
  orgId: string | null;
  memberships: OrgMembership[];
  activeOrg: OrgMembership | null;
  isLoading: boolean;
  isGlobalSuperAdmin: boolean;
  setActiveOrg: (id: string) => void;
}

const Ctx = createContext<ActiveOrgState | null>(null);

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(id: string | null) {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

/**
 * Provider for the signed-in user's active organization. Resolution order:
 *   1. localStorage["hibane.activeOrgId"] (if still a member),
 *   2. most-recent membership (matches legacy `usePrimaryOrgId` behaviour).
 *
 * Switching orgs invalidates the entire React Query cache so per-org keys
 * refetch against the new tenant, and posts to `BroadcastChannel("hibane-org-state")`
 * so other tabs follow.
 */
export function ActiveOrgProvider({ children }: { children: ReactNode }) {
  const { userId, isReady } = useAuthReady();
  const qc = useQueryClient();
  const [overrideId, setOverrideId] = useState<string | null>(() => readStored());

  const membershipsQ = useQuery({
    queryKey: ["active-org", "memberships", userId],
    enabled: !!userId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<OrgMembership[]> => {
      if (!userId) return [];
      const client = supabase as unknown as { from: (t: string) => any };
      const { data, error } = await client
        .from("organization_members")
        .select("org_id, member_role, joined_at, created_at, organizations:org_id ( id, name, slug, logo_url )")
        .is("deactivated_at", null)
        .eq("user_id", userId)
        .order("created_at", { ascending: false });
      if (error) {
        // eslint-disable-next-line no-console
        console.warn("[useActiveOrg] memberships read failed", error);
        return [];
      }
      const rows = (data ?? []) as Array<{
        org_id: string;
        member_role: string | null;
        joined_at: string | null;
        organizations: { name?: string; slug?: string | null; logo_url?: string | null } | null;
      }>;
      return rows
        .filter((r) => !!r.org_id)
        .map((r) => ({
          org_id: r.org_id,
          name: r.organizations?.name ?? "Untitled organization",
          slug: r.organizations?.slug ?? null,
          logo_url: r.organizations?.logo_url ?? null,
          member_role: r.member_role ?? null,
          joined_at: r.joined_at ?? null,
        }));
    },
  });

  const memberships = membershipsQ.data ?? [];

  const resolvedOrgId = useMemo(() => {
    if (!memberships.length) return null;
    if (overrideId && memberships.some((m) => m.org_id === overrideId)) return overrideId;
    return memberships[0].org_id;
  }, [memberships, overrideId]);

  // Defensive global-super-admin fallback, mirroring legacy hook.
  const superQuery = useQuery({
    queryKey: ["active-org", "super-fallback", userId],
    enabled: !!userId && membershipsQ.isFetched && memberships.length === 0,
    staleTime: 60_000,
    queryFn: async (): Promise<boolean> => {
      if (!userId) return false;
      try {
        const rpc = supabase.rpc as unknown as (
          fn: string,
          args: Record<string, unknown>,
        ) => Promise<{ data: unknown; error: unknown }>;
        const { data, error } = await rpc("has_role", { _user_id: userId, _role: "super_admin" });
        return !error && data === true;
      } catch {
        return false;
      }
    },
  });

  // Cross-tab sync: another tab switched org → mirror the override here.
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const ch = new BroadcastChannel(CHANNEL);
    ch.onmessage = (e: MessageEvent) => {
      const data = e.data as { type?: string; orgId?: string } | null;
      if (data?.type !== "active-org-changed") return;
      if (data.orgId && memberships.some((m) => m.org_id === data.orgId)) {
        setOverrideId(data.orgId);
        qc.invalidateQueries();
      }
    };
    return () => ch.close();
  }, [memberships, qc]);

  const setActiveOrg = useCallback(
    (id: string) => {
      if (!memberships.some((m) => m.org_id === id)) return;
      setOverrideId(id);
      writeStored(id);
      try {
        if (typeof BroadcastChannel !== "undefined") {
          const ch = new BroadcastChannel(CHANNEL);
          ch.postMessage({ type: "active-org-changed", orgId: id });
          ch.close();
        }
      } catch {
        /* noop */
      }
      qc.invalidateQueries();
    },
    [memberships, qc],
  );

  const activeOrg = useMemo(
    () => memberships.find((m) => m.org_id === resolvedOrgId) ?? null,
    [memberships, resolvedOrgId],
  );

  const value: ActiveOrgState = {
    orgId: resolvedOrgId,
    memberships,
    activeOrg,
    isLoading: !isReady || (!!userId && membershipsQ.isLoading),
    isGlobalSuperAdmin: superQuery.data === true,
    setActiveOrg,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Returns the active org context. Falls back to a degraded state when the
 * provider isn't mounted (so the legacy `usePrimaryOrgId` shim still works
 * during incremental rollout).
 */
export function useActiveOrg(): ActiveOrgState {
  const ctx = useContext(Ctx);
  if (ctx) return ctx;
  return {
    orgId: null,
    memberships: [],
    activeOrg: null,
    isLoading: false,
    isGlobalSuperAdmin: false,
    setActiveOrg: () => {
      /* no provider mounted */
    },
  };
}

export default useActiveOrg;